/**
 * Limites do filtro de período da aba "Meus Estudos" (Flux Personalizado, #1990).
 *
 * O dia de "hoje" é o dia civil de America/Sao_Paulo (nota do design, .dc.html:35-37 e 54-55),
 * igual para quem abre a tela de qualquer fuso, e o resto é aritmética de dia civil: os limites
 * saem em `DateOnly` e o servidor converte para UTC (D17). Nada aqui usa o fuso do processo:
 * `todayInSaoPaulo` lê o relógio com `Intl` no fuso explícito, e somar dias ou achar a
 * segunda-feira usa `Date.UTC`/`getUTC*`, que não dependem de `TZ`.
 */

import { DateOnly } from 'types/Flux/Personalized/myStudies';
import { MyStudiesDateInterval, MyStudiesPeriod } from 'types/Flux/Personalized/myStudiesFilters';

/** 'en-CA' formata ano-mês-dia; as partes são montadas à mão para não depender do padrão do ICU. */
const SAO_PAULO_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Sao_Paulo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const pad = (value: number) => String(value).padStart(2, '0');

/** 'YYYY-MM-DD' de um `Date.UTC(...)`: as partes UTC dele são o próprio dia civil. */
function fromUtcDate(date: Date): DateOnly {
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}` as DateOnly;
}

function dayParts(day: DateOnly): [number, number, number] {
  const [year, month, date] = day.split('-').map(Number);
  return [year, month, date];
}

/**
 * Dia civil de hoje em São Paulo.
 *
 * @param now - Agora; o chamador lê o relógio (`new Date()`) a cada uso, para "hoje" não envelhecer
 *   depois da meia-noite.
 * @returns 'YYYY-MM-DD' em America/Sao_Paulo (21h de SP já é o dia seguinte em UTC, mas não aqui).
 */
export function todayInSaoPaulo(now: Date): DateOnly {
  const parts = SAO_PAULO_DAY.formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) => (
    parts.find((item) => item.type === type)?.value ?? ''
  );
  return `${part('year')}-${part('month')}-${part('day')}` as DateOnly;
}

/**
 * Soma dias a um dia civil.
 *
 * @param day - Dia civil 'YYYY-MM-DD'.
 * @param days - Quantos dias somar (negativo volta).
 * @returns O novo dia civil, atravessando mês, ano e fevereiro bissexto.
 */
export function addDays(day: DateOnly, days: number): DateOnly {
  const [year, month, date] = dayParts(day);
  return fromUtcDate(new Date(Date.UTC(year, month - 1, date + days)));
}

/**
 * @param day - Dia civil 'YYYY-MM-DD'.
 * @returns O dia 1 do mesmo mês.
 */
export function firstDayOfMonth(day: DateOnly): DateOnly {
  const [year, month] = dayParts(day);
  return `${year}-${pad(month)}-01` as DateOnly;
}

/**
 * @param day - Dia civil 'YYYY-MM-DD'.
 * @returns O último dia do mesmo mês (28, 29, 30 ou 31).
 */
export function lastDayOfMonth(day: DateOnly): DateOnly {
  const [year, month] = dayParts(day);
  // Dia 0 do mês seguinte = último dia deste mês
  return fromUtcDate(new Date(Date.UTC(year, month, 0)));
}

/**
 * Intervalo com que "Intervalo…" abre quando ainda não há um aplicado (.dc.html:570-571, D23).
 *
 * @param todaySP - Hoje em São Paulo (`todayInSaoPaulo`).
 * @returns {1º dia do mês, hoje}.
 */
export function defaultInterval(todaySP: DateOnly): MyStudiesDateInterval {
  return { from: firstDayOfMonth(todaySP), to: todaySP };
}

/** Segunda a domingo da semana que contém o dia. */
function weekOf(day: DateOnly): MyStudiesDateInterval {
  const [year, month, date] = dayParts(day);
  // getUTCDay: 0 = domingo ... 6 = sábado; dias desde a segunda-feira
  const daysSinceMonday = (new Date(Date.UTC(year, month - 1, date)).getUTCDay() + 6) % 7;
  const monday = addDays(day, -daysSinceMonday);
  return { from: monday, to: addDays(monday, 6) };
}

/**
 * Limites do período em dias civis de São Paulo, inclusivos.
 *
 * @param periodo - Preset relativo (D17) ou 'intervalo'.
 * @param intervalo - Intervalo aplicado; só é usado quando `periodo` é 'intervalo'.
 * @param now - Agora (o chamador passa `new Date()`).
 * @returns {from, to}: hoje; semana de segunda a domingo; mês do dia 1 ao último dia; o próprio
 *   intervalo; ou `null` em 'tudo' (sem limite).
 */
export function periodRange(
  periodo: MyStudiesPeriod,
  intervalo: MyStudiesDateInterval,
  now: Date,
): MyStudiesDateInterval | null {
  if (periodo === 'tudo') return null;
  if (periodo === 'intervalo') return { from: intervalo.from, to: intervalo.to };

  const today = todayInSaoPaulo(now);
  if (periodo === 'hoje') return { from: today, to: today };
  if (periodo === 'semana') return weekOf(today);
  return { from: firstDayOfMonth(today), to: lastDayOfMonth(today) };
}
